import { useState } from "react";
import { Database, Cpu, Radio, TrendingUp, ChevronDown } from "lucide-react";
import { COLORS } from "../styles/tokens";
import { useViewport } from "../hooks";
import { ENVIRONMENTS } from "../data/packages";

interface SectionWrapProps {
  children: React.ReactNode;
  markerNum?: string;
  markerLabel?: string;
}

export function SectionWrap({ children, markerNum, markerLabel }: SectionWrapProps) {
  const { isMobile } = useViewport();
  return (
    <section style={{ padding: isMobile ? "40px 16px 80px" : "64px 32px", position: "relative" }}>
      {!isMobile && markerNum && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 64,
            left: 32,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              color: COLORS.inkMute,
              letterSpacing: "0.15em",
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
            }}
          >
            {markerLabel}
          </div>
          <div style={{ width: 1, height: 80, background: `linear-gradient(${COLORS.borderHi}, transparent)` }} />
        </div>
      )}
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>{children}</div>
    </section>
  );
}

interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  version?: string;
  isMobile: boolean;
}

export function SectionHeader({ eyebrow, title, version, isMobile }: SectionHeaderProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: isMobile ? "flex-start" : "baseline",
        justifyContent: "space-between",
        marginBottom: isMobile ? 24 : 32,
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 8 : 0,
      }}
    >
      <div>
        <div className="mono" style={{ fontSize: 11, color: COLORS.cyan, letterSpacing: "0.15em", marginBottom: 8 }}>
          {eyebrow}
        </div>
        <h2 className="display" style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, margin: 0 }}>
          {title}
        </h2>
      </div>
      {version && (
        <span className="mono" style={{ fontSize: 11, color: COLORS.inkMute, letterSpacing: "0.1em" }}>
          {version}
        </span>
      )}
    </div>
  );
}

interface EnvironmentsProps {
  isMobile: boolean;
}

export function Environments({ isMobile }: EnvironmentsProps) {
  return (
    <section
      className="glass"
      style={{
        padding: isMobile ? "48px 16px" : "60px 32px",
        borderTop: `1px solid ${COLORS.border}`,
        borderBottom: `1px solid ${COLORS.border}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {!isMobile && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 24,
            right: 32,
            color: COLORS.borderHi,
            opacity: 0.5,
          }}
        >
          <svg width="48" height="48" viewBox="0 0 48 48">
            <path d="M0 24 L48 24 M24 0 L24 48" stroke="currentColor" strokeWidth="1" />
            <circle cx="24" cy="24" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
          </svg>
        </div>
      )}

      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <SectionHeader eyebrow="◆ ACTIVATION SURFACE" title="Where signals land." isMobile={isMobile} />
        <p
          style={{
            color: COLORS.inkDim,
            fontSize: isMobile ? 14 : 16,
            maxWidth: 600,
            margin: "0 0 32px",
            lineHeight: 1.55,
          }}
        >
          Every package is mappable across our supported environments. CTV is the primary
          surface; extended channels mirror the same deal-ID and KV constructs.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {ENVIRONMENTS.map((env, i) => (
            <div
              key={i}
              style={{
                background: COLORS.bgCard,
                backdropFilter: "blur(10px)",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                padding: 20,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {env.coverage === "Primary" && (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 60,
                    height: 60,
                    background: `radial-gradient(circle at top left, ${COLORS.cyan}15, transparent 70%)`,
                    pointerEvents: "none",
                  }}
                />
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div className="display" style={{ fontSize: 18, fontWeight: 600 }}>{env.name}</div>
                <span
                  className="mono"
                  style={{
                    fontSize: 9,
                    letterSpacing: "0.1em",
                    padding: "3px 7px",
                    borderRadius: 3,
                    background: env.coverage === "Primary" ? `${COLORS.cyan}22` : "rgba(10, 19, 32, 0.6)",
                    color: env.coverage === "Primary" ? COLORS.cyan : COLORS.inkDim,
                    border: `1px solid ${env.coverage === "Primary" ? COLORS.cyan + "44" : COLORS.border}`,
                  }}
                >
                  {env.coverage.toUpperCase()}
                </span>
              </div>
              <p style={{ fontSize: 13, color: COLORS.inkDim, margin: 0, lineHeight: 1.5 }}>{env.note}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface PipelineProps {
  isMobile: boolean;
}

export function Pipeline({ isMobile }: PipelineProps) {
  const steps = [
    { step: "01", title: "Ingest", desc: "70+ data sources normalized. Weather radar, sports state, filings, indices, sentiment.", icon: Database },
    { step: "02", title: "Score", desc: "Each event scored for intent strength, recency, geo specificity, and audience overlap.", icon: Cpu },
    { step: "03", title: "Emit", desc: "Signals exposed as deal IDs, audience KVs, or curated PMPs with explicit TTL governance.", icon: Radio },
    { step: "04", title: "Activate", desc: "Buy through your DSP seat or our managed service. Reporting via dashboard or API.", icon: TrendingUp },
  ];

  return (
    <section style={{ padding: isMobile ? "48px 16px 80px" : "80px 32px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <SectionHeader eyebrow="◆ PIPELINE" title="The standard path." isMobile={isMobile} />
        <div style={{ marginTop: isMobile ? 8 : 16 }} />

        {isMobile ? (
          <div style={{ position: "relative", paddingLeft: 32 }}>
            <div
              style={{
                position: "absolute",
                left: 12,
                top: 12,
                bottom: 12,
                width: 1,
                background: `linear-gradient(180deg, ${COLORS.cyan}, ${COLORS.borderHi}, transparent)`,
              }}
            />
            {steps.map((s, i) => (
              <div key={i} style={{ marginBottom: 32, position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    left: -24,
                    top: 4,
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: COLORS.bg,
                    border: `2px solid ${COLORS.cyan}`,
                    boxShadow: `0 0 12px ${COLORS.cyan}66`,
                  }}
                />
                <div className="mono" style={{ fontSize: 11, color: COLORS.cyan, letterSpacing: "0.1em", marginBottom: 8 }}>
                  {s.step}
                </div>
                <s.icon size={20} color={COLORS.ink} style={{ marginBottom: 12 }} />
                <div className="display" style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>
                  {s.title}
                </div>
                <p style={{ fontSize: 14, color: COLORS.inkDim, lineHeight: 1.55, margin: 0 }}>
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, position: "relative", marginTop: 32 }}>
            {steps.map((s, i, arr) => (
              <div
                key={i}
                style={{
                  padding: "0 20px 0 0",
                  borderRight: i < arr.length - 1 ? `1px solid ${COLORS.border}` : "none",
                  paddingLeft: i > 0 ? 20 : 0,
                  position: "relative",
                }}
              >
                <div className="mono" style={{ fontSize: 11, color: COLORS.cyan, letterSpacing: "0.1em", marginBottom: 16 }}>
                  {s.step}
                </div>
                <s.icon size={20} color={COLORS.ink} style={{ marginBottom: 16 }} />
                <div className="display" style={{ fontSize: 22, fontWeight: 600, marginBottom: 10 }}>
                  {s.title}
                </div>
                <p style={{ fontSize: 13, color: COLORS.inkDim, lineHeight: 1.55, margin: 0 }}>
                  {s.desc}
                </p>
                {i < arr.length - 1 && (
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      right: -4,
                      top: 22,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: COLORS.cyan,
                      boxShadow: `0 0 12px ${COLORS.cyan}`,
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    q: "What is RealSynq?",
    a: "RealSynq, by Leanback Digital, is a real-time signal layer that converts 70+ live real-world data sources into TTL-bound, machine-readable intent signals delivered as deal IDs and audience keys for any major DSP or SSP.",
  },
  {
    q: "What signal packages does RealSynq offer?",
    a: "Eight packages: Sports, Women's Sports, Weather, Gaming, Entertainment, Luxury, Finance, and Civic. Each groups related real-world signals with explicit TTL, geo specificity, and intent scoring.",
  },
  {
    q: "How are RealSynq signals activated?",
    a: "Through Deal IDs, Audience Key-Values, Curated PMP, and Geo-Fenced PMP across CTV/OTT, MVPD, online video, OOH/DOOH, and audio. Signals are injected into the OpenRTB bidstream at request time and auto-expire when the real-world moment is no longer true.",
  },
  {
    q: "What is a TTL-bound signal?",
    a: "A TTL-bound signal carries an explicit time-to-live and stops delivering when the real-world moment is no longer true — for example when a storm clears or a close game ends. This prevents stale activation and keeps spend aligned to live conditions.",
  },
  {
    q: "Does RealSynq use personal data or PII?",
    a: "No. RealSynq uses no PII and no individual-level targeting. Geo resolution stops at the Nielsen DMA, and activation is event-based real-world context rather than identity.",
  },
  {
    q: "Which DSPs and SSPs does RealSynq support?",
    a: "RealSynq signals activate on any major DSP via deal IDs and audience keys, across marketplaces including TripleLift, Nexxen, Index Exchange, SOVRN, Amazon, and Amagi.",
  },
  {
    q: "What is the RealSynq Women's Sports package?",
    a: "A curated package of female-led domestic sports moments across the WNBA, NWSL, NCAA (basketball, volleyball, softball), LPGA, and Team USA/Olympic events, delivered as TTL-bound deal IDs and audience keys.",
  },
  {
    q: "How do I onboard with RealSynq?",
    a: "Submit a seat-mapping request through the Onboarding tab on the RealSynq Explorer at signals.leanback.digital. Leanback Digital maps your DSP seats to the deal IDs and audience keys for your selected packages.",
  },
];

function FAQItem({ q, a, isMobile }: { q: string; a: string; isMobile: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        background: COLORS.bgCard,
        backdropFilter: "blur(10px)",
        border: `1px solid ${open ? COLORS.borderHi : COLORS.border}`,
        borderRadius: 8,
        overflow: "hidden",
        transition: "border-color 0.2s",
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          padding: isMobile ? "16px 16px" : "18px 24px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
        aria-expanded={open}
      >
        <span
          className="display"
          style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: COLORS.ink, lineHeight: 1.4 }}
        >
          {q}
        </span>
        <ChevronDown
          size={16}
          color={COLORS.cyan}
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
        />
      </button>
      {open && (
        <div
          style={{
            padding: isMobile ? "12px 16px 16px" : "14px 24px 18px",
            color: COLORS.inkDim,
            fontSize: isMobile ? 13 : 14,
            lineHeight: 1.65,
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          {a}
        </div>
      )}
    </div>
  );
}

interface FAQSectionProps {
  isMobile: boolean;
}

export function FAQSection({ isMobile }: FAQSectionProps) {
  return (
    <section
      className="glass"
      style={{
        padding: isMobile ? "48px 16px 80px" : "80px 32px",
        borderTop: `1px solid ${COLORS.border}`,
        position: "relative",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <SectionHeader eyebrow="◆ FAQ" title="Common questions." isMobile={isMobile} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? 8 : 12,
            marginTop: isMobile ? 8 : 16,
          }}
        >
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} isMobile={isMobile} />
          ))}
        </div>
      </div>
    </section>
  );
}
